/* Admin Design System — shared premium primitives.
   Dark-first, no emoji icons, lucide-react only.
   All modals/drawers: focus autofocus, Escape to close, ARIA labelled, reduced-motion aware.
   All destructive flows: typed reason (min 3), optional typed-confirmation.
   Do NOT introduce component-specific variants; extend here so every page stays uniform. */
import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, XCircle, Info, Loader2, ChevronRight, ChevronLeft,
  Copy, Check, X, ArrowLeft, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

// ------------------------------------------------------------------ PageHeader
export function PageHeader({ title, description, breadcrumbs, badge, primary, secondary, tertiary, meta, testid }) {
  return (
    <header className="mb-6 space-y-3" data-testid={testid || "page-header"}>
      {breadcrumbs?.length > 0 && (
        <nav className="text-xs text-white/40 flex items-center gap-1 flex-wrap" aria-label="breadcrumb">
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-3 h-3 opacity-60" />}
              {b.to ? <Link to={b.to} className="hover:text-white/70">{b.label}</Link> : <span className="text-white/70">{b.label}</span>}
            </React.Fragment>
          ))}
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-2xl sm:text-3xl text-white/95 tracking-tight">{title}</h1>
            {badge}
          </div>
          {description && <p className="text-white/50 text-sm mt-1 max-w-2xl">{description}</p>}
          {meta && <p className="text-white/40 text-xs mt-1">{meta}</p>}
        </div>
        {(primary || secondary || tertiary) && (
          <div className="flex items-center gap-2 flex-wrap shrink-0" data-testid="page-header-actions">
            {tertiary}
            {secondary}
            {primary}
          </div>
        )}
      </div>
    </header>
  );
}

// ------------------------------------------------------------------ StatusBadge / AutoBadge
const TONE_MAP = {
  success: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  warning: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  danger:  "bg-red-500/15 text-red-200 border-red-500/30",
  info:    "bg-sky-500/15 text-sky-200 border-sky-500/30",
  neutral: "bg-white/5 text-white/60 border-white/10",
  brand:   "bg-white/10 text-white/90 border-white/15",
};
export function StatusBadge({ tone = "neutral", icon: Icon, children, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border whitespace-nowrap ${TONE_MAP[tone]}`}>
      {Icon && <Icon className="w-3 h-3" aria-hidden="true" />}
      {children}
    </span>
  );
}
const STATUS_TONE = {
  active: "success", published: "success", ok: "success", healthy: "success", success: "success", enabled: "success",
  completed: "success", resolved: "success", verified: "success", processed: "success", won: "success",
  draft: "warning", pending: "warning", queued: "warning", degraded: "warning", investigating: "warning",
  warning: "warning", low: "warning", medium: "warning", frozen: "warning", maintenance: "warning",
  failed: "danger", error: "danger", down: "danger", critical: "danger", high: "danger", revoked: "danger",
  suspended: "danger", suspicious: "danger", lost: "danger", inactive: "neutral",
  archived: "neutral", cancelled: "neutral", closed: "neutral", disabled: "neutral", untested: "neutral",
  info: "info", informational: "info", monitoring: "info", identified: "info", not_configured: "neutral",
};
export function AutoBadge({ value, testid }) {
  if (value == null || value === "") return null;
  const key = String(value).toLowerCase();
  return <StatusBadge tone={STATUS_TONE[key] || "neutral"} testid={testid}>{value}</StatusBadge>;
}

// ------------------------------------------------------------------ EmptyState / ErrorState
export function EmptyState({ icon: Icon = Info, title, description, action, testid }) {
  return (
    <div className="lp-card p-10 text-center space-y-3" data-testid={testid || "empty-state"}>
      <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-white/5 border border-white/10 mx-auto">
        <Icon className="w-5 h-5 text-white/50" aria-hidden="true" />
      </div>
      <div className="font-display text-lg text-white/85">{title}</div>
      {description && <p className="text-white/50 text-sm max-w-md mx-auto">{description}</p>}
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}
export function ErrorState({ error, retry, testid }) {
  const trace = error?.response?.headers?.["x-trace-id"] || error?.response?.data?.trace_id;
  const msg = formatApiErrorDetail(error) || "Something went wrong.";
  return (
    <div className="lp-card p-6 space-y-3" data-testid={testid || "error-state"}>
      <div className="flex items-start gap-3">
        <div className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-red-500/10 border border-red-500/30">
          <XCircle className="w-5 h-5 text-red-200" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-white/95">Could not load</div>
          <p className="text-white/60 text-sm mt-1 break-words">{msg}</p>
          {trace && (
            <button onClick={() => { navigator.clipboard?.writeText(trace); toast.success("Trace ID copied"); }}
              className="mt-2 inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-white/70">
              <Copy className="w-3 h-3" /> trace: <span className="font-mono">{trace}</span>
            </button>
          )}
        </div>
        {retry && <button className="lp-btn-secondary text-xs" onClick={retry} data-testid="error-retry">Retry</button>}
      </div>
    </div>
  );
}
export function NotConfigured({ label, hint, testid }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/60 flex items-start gap-2" data-testid={testid || "not-configured"}>
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <div>
        <div className="text-white/80">{label || "Not configured"}</div>
        {hint && <div className="text-white/50 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Skeleton
export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div className="lp-card overflow-hidden" aria-busy="true" aria-live="polite" data-testid="table-skeleton">
      <div className="p-4 space-y-3">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
            {Array.from({ length: cols }).map((_, c) => (
              <div key={c} className="lp-shimmer rounded h-4" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
export function CardSkeleton({ h = "h-32" }) {
  return <div className={`${h} lp-shimmer rounded-2xl`} aria-busy="true" data-testid="card-skeleton" />;
}

// ------------------------------------------------------------------ Modal shell + focus trap
function useEscape(open, onClose) {
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
}

export function Modal({ open, onClose, title, description, children, footer, size = "md", testid, danger }) {
  useEscape(open, onClose);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (open) setTimeout(() => ref.current?.querySelector("[data-autofocus],input,textarea,button")?.focus(), 30);
  }, [open]);
  if (!open) return null;
  const w = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" }[size];
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 motion-safe:animate-in motion-safe:fade-in-0"
      role="dialog" aria-modal="true" aria-labelledby="modal-title" data-testid={testid}>
      <div ref={ref} className={`lp-card ${w} w-full p-6 space-y-4 motion-safe:animate-in motion-safe:zoom-in-95 max-h-[92vh] overflow-y-auto`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div id="modal-title" className={`font-display text-lg ${danger ? "text-red-200" : "text-white/95"} flex items-center gap-2`}>
              {danger && <ShieldAlert className="w-4 h-4 text-red-300" />}
              {title}
            </div>
            {description && <p className="text-white/50 text-xs mt-1">{description}</p>}
          </div>
          <button className="text-white/40 hover:text-white/80 shrink-0" aria-label="Close" onClick={onClose} data-testid="modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-white/5">{footer}</div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Drawer (right-side)
export function Drawer({ open, onClose, title, subtitle, children, actions, size = "md", testid }) {
  useEscape(open, onClose);
  if (!open) return null;
  const w = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-3xl", xl: "max-w-5xl" }[size];
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex justify-end motion-safe:animate-in motion-safe:fade-in-0" role="dialog" aria-modal="true" onClick={onClose} data-testid={testid}>
      <div className={`w-full ${w} h-full bg-[#141417] border-l border-white/10 overflow-y-auto motion-safe:animate-in motion-safe:slide-in-from-right-4`}
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#141417]/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {subtitle && <div className="text-white/40 text-xs">{subtitle}</div>}
            <div className="font-display text-lg text-white/95">{title}</div>
          </div>
          <button className="lp-btn-ghost" aria-label="Close" onClick={onClose} data-testid="drawer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">{children}</div>
        {actions && <div className="sticky bottom-0 bg-[#141417]/95 backdrop-blur border-t border-white/5 px-6 py-3 flex justify-end gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ ReasonModal — always-typed reason
export function ReasonModal({ open, onClose, onSubmit, title, description, actionLabel, danger, minLen = 3, extras, requireTyped, typedMatch, requireCheck, checkLabel }) {
  const [reason, setReason] = React.useState("");
  const [typed, setTyped] = React.useState("");
  const [checked, setChecked] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (!open) { setReason(""); setTyped(""); setChecked(false); setBusy(false); } }, [open]);
  const typedOk = !requireTyped || typed.trim() === typedMatch;
  const checkOk = !requireCheck || checked;
  const disabled = reason.trim().length < minLen || !typedOk || !checkOk || busy;
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(reason.trim()); onClose?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={title} description={description} danger={danger} testid="reason-modal"
      footer={<>
        <button className="lp-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className={danger ? "lp-btn-danger" : "lp-btn-primary"} disabled={disabled} onClick={submit} data-testid="reason-submit">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {actionLabel}
        </button>
      </>}>
      {extras}
      {requireTyped && (
        <label className="block space-y-1">
          <span className="text-white/70 text-xs">Type <span className="font-mono text-white/90">{typedMatch}</span> to confirm</span>
          <input className="lp-input w-full" data-autofocus value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="reason-typed" autoComplete="off" />
        </label>
      )}
      {requireCheck && (
        <label className="flex items-start gap-2 text-white/80 text-xs">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="reason-check" />
          <span>{checkLabel}</span>
        </label>
      )}
      <label className="block space-y-1">
        <span className="text-white/70 text-xs">Reason (audit log, min {minLen} chars) *</span>
        <textarea className="lp-input w-full h-20" data-autofocus={!requireTyped} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reason-input" />
      </label>
    </Modal>
  );
}

// ------------------------------------------------------------------ SecretRevealModal — reveal-once, copy, no download
export function SecretRevealModal({ open, onClose, secret, title = "One-time secret", meta }) {
  const [copied, setCopied] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  React.useEffect(() => { if (!open) { setCopied(false); setConfirm(false); } }, [open]);
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(secret); setCopied(true); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed — select and copy manually."); }
  };
  const closeGuarded = () => {
    if (!confirm) { toast.warning("Confirm you saved the secret before closing."); return; }
    onClose?.();
  };
  return (
    <Modal open={open} onClose={closeGuarded} title={title} description="This is the ONLY time this value will be shown. Copy it now — closing this dialog discards it." danger testid="secret-reveal">
      <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-amber-100 text-xs flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>The raw secret is never logged, never stored in cleartext, and never re-exposed. Rotate again if lost.</div>
      </div>
      <div className="rounded-lg bg-black/40 border border-white/10 p-3 font-mono text-xs text-white/95 break-all select-all" data-testid="secret-value">{secret}</div>
      {meta && <div className="text-white/40 text-[10px]">{meta}</div>}
      <div className="flex items-center gap-3 flex-wrap">
        <button className="lp-btn-primary text-xs" onClick={doCopy} data-testid="secret-copy">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy secret"}
        </button>
        <label className="text-xs text-white/70 flex items-center gap-1">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="secret-confirm" />
          I saved the secret in a safe place
        </label>
      </div>
      <div className="flex justify-end">
        <button className="lp-btn-secondary text-xs" onClick={closeGuarded} disabled={!confirm} data-testid="secret-done">I'm done</button>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ ConfirmModal — replaces window.confirm
export function ConfirmModal({ open, onClose, onConfirm, title, description, actionLabel = "Confirm", danger, requireTyped, typedMatch }) {
  const [typed, setTyped] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (!open) { setTyped(""); setBusy(false); } }, [open]);
  const disabled = busy || (requireTyped && typed.trim() !== typedMatch);
  const submit = async () => {
    setBusy(true);
    try { await onConfirm(); onClose?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={title} description={description} danger={danger} testid="confirm-modal"
      footer={<>
        <button className="lp-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className={danger ? "lp-btn-danger" : "lp-btn-primary"} onClick={submit} disabled={disabled} data-testid="confirm-submit">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {actionLabel}
        </button>
      </>}>
      {requireTyped && (
        <label className="block space-y-1">
          <span className="text-white/70 text-xs">Type <span className="font-mono text-white/90">{typedMatch}</span> to confirm</span>
          <input className="lp-input w-full" data-autofocus value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="confirm-typed" autoComplete="off" />
        </label>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------------ Toolbar + Pagination + Tabs
export function Toolbar({ children, right, testid }) {
  return (
    <div className="lp-card p-3 flex flex-wrap items-center gap-2" data-testid={testid || "toolbar"}>
      <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">{children}</div>
      {right && <div className="flex items-center gap-2 ml-auto flex-wrap">{right}</div>}
    </div>
  );
}

export function Pagination({ page, totalPages, onChange, total, unitLabel = "rows", testid }) {
  const safeTotal = Math.max(1, totalPages || 1);
  return (
    <div className="flex items-center justify-between text-xs text-white/50" data-testid={testid || "pagination"}>
      <div>{total != null ? `${total.toLocaleString()} ${unitLabel}` : ""}</div>
      <div className="flex items-center gap-1">
        <button disabled={page <= 1} onClick={() => onChange(page - 1)}
          className="lp-btn-ghost disabled:opacity-40" data-testid="pagination-prev" aria-label="Previous page">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="px-2 tabular-nums">{page} / {safeTotal}</span>
        <button disabled={page >= safeTotal} onClick={() => onChange(page + 1)}
          className="lp-btn-ghost disabled:opacity-40" data-testid="pagination-next" aria-label="Next page">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export function TabList({ tabs, value, onChange, testid }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current?.querySelector(`[data-tab="${value}"]`);
    if (el) el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [value]);
  return (
    <div ref={ref} className="flex gap-1 border-b border-white/10 overflow-x-auto no-scrollbar" role="tablist" data-testid={testid || "tab-list"}>
      {tabs.map((t) => (
        <button key={t.id} role="tab" data-tab={t.id} aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={`px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${value === t.id ? "text-white border-b-2 border-white" : "text-white/50 hover:text-white/80 border-b-2 border-transparent"}`}
          data-testid={`tab-${t.id}`}>
          {t.icon && <t.icon className="inline w-3.5 h-3.5 mr-1" />}
          {t.label}
          {t.count != null && <span className="ml-1.5 text-[10px] text-white/40">({t.count})</span>}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ Cells / helpers
export function CopyId({ id, label, chars = 8 }) {
  if (!id) return <span className="text-white/30">—</span>;
  const short = String(id).length > chars ? `${String(id).slice(0, chars)}…` : String(id);
  return (
    <button title={String(id)} onClick={() => { navigator.clipboard?.writeText(id); toast.success((label || "Value") + " copied"); }}
      className="font-mono text-xs text-white/70 hover:text-white inline-flex items-center gap-1">
      {short}<Copy className="w-3 h-3 opacity-40" />
    </button>
  );
}
export function DateCell({ value, mode = "datetime" }) {
  if (!value) return <span className="text-white/30">—</span>;
  try {
    const d = new Date(value);
    const s = mode === "date" ? d.toLocaleDateString() : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    return <span className="text-white/60 tabular-nums whitespace-nowrap">{s}</span>;
  } catch { return <span className="text-white/60">{String(value)}</span>; }
}
export function Money({ cents, sign, testid }) {
  if (cents == null) return <span className="text-white/30">—</span>;
  const v = Number(cents) / 100;
  const cls = sign && cents < 0 ? "text-red-300" : sign && cents > 0 ? "text-emerald-300" : "";
  return <span className={`tabular-nums ${cls}`} data-testid={testid}>{v.toLocaleString(undefined, { style: "currency", currency: "USD" })}</span>;
}
export function Num({ value, sign, testid }) {
  if (value == null) return <span className="text-white/30">—</span>;
  const n = Number(value);
  const cls = sign && n < 0 ? "text-red-300" : sign && n > 0 ? "text-emerald-300" : "";
  return <span className={`tabular-nums ${cls}`} data-testid={testid}>{sign && n > 0 ? "+" : ""}{n.toLocaleString()}</span>;
}

// ------------------------------------------------------------------ SectionCard
export function SectionCard({ title, right, description, children, testid, padding = "p-4" }) {
  return (
    <section className={`lp-card ${padding} space-y-3`} data-testid={testid}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            {title && <h2 className="text-white/85 text-sm font-semibold tracking-wide">{title}</h2>}
            {description && <p className="text-white/40 text-xs mt-0.5">{description}</p>}
          </div>
          {right && <div className="flex items-center gap-2 shrink-0">{right}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// ------------------------------------------------------------------ Kpi cell
export function Kpi({ label, value, sub, tone, icon: Icon, testid }) {
  const c = tone === "danger" ? "text-red-300"
    : tone === "warning" ? "text-amber-300"
    : tone === "success" ? "text-emerald-300"
    : "text-white/95";
  return (
    <div className="lp-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-white/30" />}
      </div>
      <div className={`font-display text-2xl mt-1 tabular-nums ${c}`}>{value}</div>
      {sub && <div className="text-white/40 text-[10px] mt-1">{sub}</div>}
    </div>
  );
}

// ------------------------------------------------------------------ ResponsiveTable — table wrapper with horizontal scroll
export function ResponsiveTable({ children, minWidth = 900, testid }) {
  return (
    <div className="lp-card overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="lp-table text-xs w-full" style={{ minWidth: `${minWidth}px` }}>{children}</table>
      </div>
    </div>
  );
}

export const AdminIcons = { Info, AlertTriangle, CheckCircle2, XCircle, ArrowLeft };
